import React, { useState, useRef, useCallback, useEffect } from "react";
import { Camera, RotateCcw, Check, Image, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export default function CameraModal({ onCapture, onClose }) {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState("user");

  const startCamera = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = s;
      }
      setStream(s);
      setCameraActive(true);
    } catch (e) {
      console.error("Camera access denied:", e);
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setCameraActive(false);
  }, [stream]);

  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [stream]);

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const ox = (video.videoWidth - size) / 2;
    const oy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, ox, oy, size, size, 0, 0, size, size);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setPhoto(dataUrl);
    stopCamera();
  };

  const retake = () => {
    setPhoto(null);
    startCamera();
  };

  const confirm = () => {
    if (photo) {
      const base64 = photo.split(",")[1];
      onCapture({ data: base64, type: "image/jpeg", previewUrl: photo });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const previewUrl = URL.createObjectURL(file);
      setPhoto(previewUrl);
      stopCamera();
      // Store base64 on the photo for later
      const base64 = reader.result.split(",")[1];
      setPhoto(reader.result); // full data URL
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <h2 className="font-display text-lg text-primary font-semibold">
            {t("camera_title") || "Foto aufnehmen"}
          </h2>
          <button onClick={() => { stopCamera(); onClose(); }} className="text-text-muted hover:text-primary transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {!photo && !cameraActive && (
            <div className="flex flex-col gap-3">
              <button
                onClick={startCamera}
                className="flex items-center justify-center gap-3 py-4 bg-primary text-white rounded-xl font-medium hover:bg-primary-600 transition-colors"
              >
                <Camera size={20} />
                {t("camera_take") || "Kamera öffnen"}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center gap-3 py-4 bg-surface border border-surface-border text-primary rounded-xl font-medium hover:bg-primary-50 transition-colors"
              >
                <Image size={20} />
                {t("camera_gallery") || "Aus Galerie wählen"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {cameraActive && !photo && (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
                />
              </div>
              <div className="flex justify-center gap-4">
                <button
                  onClick={takePhoto}
                  className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-600 transition-colors shadow-lg"
                >
                  <Camera size={24} />
                </button>
              </div>
            </div>
          )}

          {photo && (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden aspect-square">
                <img src={photo} alt="Preview" className="w-full h-full object-cover" />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={retake}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-surface border border-surface-border rounded-xl text-primary font-medium hover:bg-primary-50 transition-colors"
                >
                  <RotateCcw size={16} />
                  {t("camera_retake") || "Nochmal"}
                </button>
                <button
                  onClick={confirm}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-600 transition-colors"
                >
                  <Check size={16} />
                  {t("camera_send") || "Analysieren"}
                </button>
              </div>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
